import type { CoinbaseEngUsersAccountManagementApiApiWorkspaceAuditEvent } from '@cbhq/identity-api-hooks/account-management/types';

import type { AuditLogRowEntry } from '../types';

import { ACTIVITY_DATE_FILTER_OPTIONS } from './constants';

export function mapAuditEventToRow(
  event: CoinbaseEngUsersAccountManagementApiApiWorkspaceAuditEvent,
): AuditLogRowEntry {
  return {
    actor: {
      id: event.actor?.id ?? '',
      name: event.actor?.name || null,
      email: event.actor?.email || null,
      avatarUrl: event.actor?.avatarUrl ?? '',
    },
    target: {
      id: event.target?.id ?? '',
      name: event.target?.name || null,
      email: event.target?.email || null,
      avatarUrl: event.target?.avatarUrl ?? '',
    },
    eventType: event.eventType ?? '',
    timestamp: event.timestamp ?? '',
    actorContext: {
      countryCode: event.actorContext?.countryCode ?? '',
      ipAddress: event.actorContext?.ipAddress ?? '',
      userAgent: event.actorContext?.userAgent ?? '',
      city: event.actorContext?.city ?? '',
    },
    eventId: event.eventId ?? '',
  };
}

export function getStartTimeFromDatePreset(dateFilter: string | null): string {
  if (!dateFilter) {
    return '';
  }

  const preset = ACTIVITY_DATE_FILTER_OPTIONS.find((option) => option.value === dateFilter);
  if (!preset) {
    return '';
  }

  return new Date(Date.now() - preset.hoursAgo * 60 * 60 * 1000).toISOString();
}

const LAST_90_DAYS_HOURS = 24 * 90;

export function getLast90DaysTimeRange(): { startTime: string; endTime: string } {
  const now = Date.now();
  return {
    startTime: new Date(now - LAST_90_DAYS_HOURS * 60 * 60 * 1000).toISOString(),
    endTime: new Date(now).toISOString(),
  };
}

// The DatePicker hands back the chosen day at local midnight. For the inclusive
// lower bound we keep the start of that day.
export function getExportStartTime(date: Date): string {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start.toISOString();
}

// For the inclusive upper bound we extend to the end of the chosen day so events
// from that day are captured, then clamp to now because the backend rejects an
// end_time in the future.
export function getExportEndTime(date: Date, now = new Date()): string {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return (end.getTime() > now.getTime() ? now : end).toISOString();
}

export function getExportFilename(date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const time = `${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  return `audit-export-${day}-${time}.csv`;
}

export function downloadCsvFromBase64(
  content: string,
  filename: string,
  contentType = 'text/csv',
): void {
  const bytes = Uint8Array.from(atob(content), (character) => character.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: contentType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export function truncateEventId(eventId: string): string {
  if (!eventId || eventId.length <= 1) {
    return eventId;
  }

  const halfLength = Math.ceil(eventId.length / 2);
  return `${eventId.slice(0, halfLength)}…`;
}
